import React, { useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Lock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface MarkdownProps {
  href?: string;
  children?: ReactNode;
}

const markdownComponents = {
  a: ({ href, children }: MarkdownProps) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
      {children}
    </a>
  ),
  strong: ({ children }: MarkdownProps) => <strong className="font-bold text-gray-900">{children}</strong>,
  em: ({ children }: MarkdownProps) => <em className="italic">{children}</em>,
  p: ({ children }: MarkdownProps) => <p className="mb-2">{children}</p>,
  ul: ({ children }: MarkdownProps) => <ul className="list-disc list-inside mb-2">{children}</ul>,
  ol: ({ children }: MarkdownProps) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
  li: ({ children }: MarkdownProps) => <li className="ml-2">{children}</li>,
  h1: ({ children }: MarkdownProps) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
  h2: ({ children }: MarkdownProps) => <h2 className="text-base font-bold mb-2">{children}</h2>,
  h3: ({ children }: MarkdownProps) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
};

const StreamingMessage = ({ content, onComplete, messagesEndRef }: { content: string; onComplete?: () => void; messagesEndRef?: React.RefObject<HTMLDivElement> }) => {
  const [displayedContent, setDisplayedContent] = useState('');

  useEffect(() => {
    setDisplayedContent('');
    let index = 0;
    const interval = setInterval(() => {
      if (index < content.length) {
        setDisplayedContent(content.substring(0, index + 1));
        index++;
      } else {
        clearInterval(interval);
        onComplete?.();
      }
    }, 15);

    return () => clearInterval(interval);
  }, [content, onComplete]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayedContent, messagesEndRef]);

  return (
    <ReactMarkdown components={markdownComponents}>
      {displayedContent}
    </ReactMarkdown>
  );
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface ExitIntentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  description: string;
  quickQuestions?: Array<{ q: string }>;
}

const WEBHOOK_URL = 'https://ccs.kqhlbr.easypanel.host/webhook/d0088bac-0e6c-4ab9-a24b-50b0cd0733bc/chat';

export const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({
  isOpen,
  onClose,
  description,
  quickQuestions = [
    { q: "Quelle formation est adaptée à mon équipe ?" },
    { q: "Combien coûte une formation?" },
    { q: "Es-ce vous vous déplacez dans nos locaux ?" },
    { q: "Est-ce adapté aux débutants ?" }
  ]
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [question, setQuestion] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [welcomeComplete, setWelcomeComplete] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleWelcomeComplete = useCallback(() => {
    setWelcomeComplete(true);
  }, []);

  useEffect(() => {
    const now = new Date();
    const time = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    setCurrentTime(time);

    if (!sessionId) {
      setSessionId(crypto.randomUUID());
    }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    const messageText = question.trim() || 'Lancer une nouvelle conversation';

    setMessages(prev => [...prev, {
      role: 'user',
      content: messageText,
      timestamp: currentTime
    }]);

    setQuestion('');
    setIsLoading(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'sendMessage',
          sessionId,
          chatInput: messageText
        })
      });

      const data = await response.json();

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.output,
        timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      }]);
    } catch (error) {
      console.error('Erreur webhook:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Désolée, une erreur est survenue. Veuillez réessayer.',
        timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = (questionText: string) => {
    setQuestion(questionText);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          />

          {/* Chat Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 flex items-center justify-center z-50 px-4 py-4 sm:py-0"
          >
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col w-full max-w-sm sm:max-w-md max-h-[90vh] sm:max-h-[85vh]">
              {/* Header */}
              <div className="bg-white border-b border-gray-100 p-3 sm:p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {/* Avatar */}
                    <div className="relative w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                      <img
                        src="/chloé_avatar.webp"
                        alt="Chloé"
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shadow-lg"
                      />
                      {/* Online badge */}
                      <div className="absolute bottom-0 right-0 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-white shadow-md" />
                    </div>

                    {/* Title and subtitle */}
                    <div className="min-w-0">
                      <h2 className="text-sm sm:text-base font-bold text-gray-900 truncate">
                        Chloé <span className="text-lg">✨</span>
                      </h2>
                      <p className="text-xs sm:text-xs text-gray-500 truncate">
                        En ligne · Réponse rapide
                      </p>
                    </div>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={onClose}
                    className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors hover:bg-gray-50 rounded-lg flex-shrink-0"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Messages and content area - scrollable */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                {messages.length === 0 && (
                  <>
                    {/* Initial greeting */}
                    <div className="flex gap-2 mb-1">
                      <img
                        src="/chloé_avatar.webp"
                        alt="Chloé"
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0 shadow-md"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="bg-purple-100 rounded-2xl p-2 sm:p-3 shadow-sm">
                          <StreamingMessage content={`Bonjour 👋\n\nJe suis Chloé, l’agent IA d’Upnov.\nJe peux vous aider à trouver la formation IA la plus adaptée à votre entreprise. ${description}\n\nQue souhaitez-vous savoir ?`} onComplete={handleWelcomeComplete} messagesEndRef={messagesEndRef} />
                        </div>
                        {currentTime && (
                          <p className="text-xs text-gray-400 mt-1 ml-2">{currentTime}</p>
                        )}
                      </div>
                    </div>

                    {/* Quick questions */}
                    {quickQuestions.length > 0 && welcomeComplete && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 mb-2 ml-9 uppercase tracking-wide">
                          Questions fréquentes
                        </h3>
                        <div className="grid grid-cols-2 gap-2 ml-9">
                          {quickQuestions.map((item, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleQuickQuestion(item.q)}
                              disabled={isLoading}
                              className="text-left px-2 sm:px-3 py-1.5 sm:py-2 rounded-full border-2 border-purple-300 text-purple-600 font-medium text-xs hover:bg-purple-50 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed line-clamp-2"
                            >
                              {item.q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Chat history */}
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    {msg.role === 'assistant' && (
                      <img
                        src="/chloé_avatar.webp"
                        alt="Chloé"
                        className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover flex-shrink-0 shadow-md"
                      />
                    )}
                    <div className={`flex-1 min-w-0 ${msg.role === 'user' ? 'text-right' : ''}`}>
                      <div className={`${
                        msg.role === 'assistant'
                          ? 'bg-purple-100 text-gray-900'
                          : 'bg-blue-500 text-white'
                      } rounded-2xl p-2 sm:p-3 shadow-sm inline-block max-w-xs text-xs sm:text-sm leading-snug break-words`}>
                        {msg.role === 'assistant' ? (
                          <StreamingMessage content={msg.content} messagesEndRef={messagesEndRef} />
                        ) : (
                          <p>{msg.content}</p>
                        )}
                      </div>
                      <p className={`text-xs text-gray-400 mt-0.5 ${msg.role === 'user' ? 'mr-2' : 'ml-2'}`}>
                        {msg.timestamp}
                      </p>
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="flex gap-2">
                    <img
                      src="/chloé_avatar.webp"
                      alt="Chloé"
                      className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover flex-shrink-0 shadow-md"
                    />
                    <div className="bg-purple-100 rounded-2xl p-2 sm:p-3 shadow-sm">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input section */}
              <div className="border-t border-gray-100 bg-white p-3 sm:p-4 space-y-2 sm:space-y-3">
                {/* Message input */}
                <div className="relative">
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Posez votre question..."
                    className="w-full px-3 sm:px-4 py-2 sm:py-2 pr-10 border-2 border-purple-300 rounded-full focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all resize-none text-xs sm:text-sm placeholder-gray-400"
                    rows={1}
                    style={{ minHeight: '40px' }}
                    disabled={isLoading}
                  />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2">
                    <button
                      onClick={handleSendMessage}
                      disabled={isLoading}
                      className="p-1.5 sm:p-2 text-purple-600 hover:bg-purple-100 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Send"
                    >
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>

                {/* Help text */}
                <p className="text-xs text-gray-500 text-center italic line-clamp-2">
                  Ex. Quelle formation pour mon équipe ?
                </p>

                {/* Security message */}
                <div className="flex items-center justify-center gap-1 pt-1">
                  <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-500">
                    Données sécurisées
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
